'use client'

import { useEffect } from 'react'
import { initAIReferrerTracking } from '@/lib/analytics/ai-referrer-tracker'
import { getOrCreateVisitorId } from '@/lib/analytics/visitor-id'
import { getFirstTouchUtm } from '@/lib/analytics/first-touch-utm'
import { isAnalyticsConsentGranted } from '@/lib/consent-manager'

/**
 * Detects LLM referrals (ChatGPT, Claude, Perplexity, Gemini, Copilot, Grok,
 * You.com, Poe) and persists them for attribution. Mounted once in the root
 * layout.
 *
 * The beacon fires ONLY on an LLM-referred landing, never on ordinary page
 * views — which is why the stored field is `last_llm_referral_at` rather than
 * "last visit". A visitor who arrives from ChatGPT today and returns directly
 * tomorrow keeps today's date, by design, and write volume stays at a handful
 * per visitor instead of one per pageview.
 *
 * Consent-gated twice: this component checks before doing anything, and
 * getOrCreateVisitorId re-checks the central resolver before minting or
 * returning an id.
 */
export default function AIReferrerTracker() {
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (!isAnalyticsConsentGranted()) return

    const aiReferrer = initAIReferrerTracking()
    if (!aiReferrer?.isAIReferred) return

    const visitorId = getOrCreateVisitorId()
    if (!visitorId) return

    const utm = getFirstTouchUtm()

    // Fire-and-forget. keepalive so the write survives an immediate
    // navigation away; failures are swallowed because attribution must never
    // interfere with the page.
    void fetch('/api/llm-referral', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        visitorId,
        source: aiReferrer.source,
        referrer: aiReferrer.referrer,
        landingPath: window.location.pathname,
        utmSource: utm?.utm_source ?? null,
        utmMedium: utm?.utm_medium ?? null,
        utmCampaign: utm?.utm_campaign ?? null,
      }),
    }).catch(() => {})
  }, [])

  return null
}
